import { useState, useRef, useMemo } from 'react';
import api from '../../../hooks/useApi';
import Modal from '../../shared/Modal';
import { fmtDY } from './planConstants';

/*
  Import a plan from Microsoft Project.

  Two steps, deliberately. The file is parsed on the server and shown back as
  the tree it would create, with everything the parser had to drop or
  approximate listed above it, before a single row is written. Project managers
  are not always working from the same version of a sheet, and "Replace" deletes
  a plan, so the person confirms what they are looking at first.
*/

const fmtDays = (d) => {
  if (d === null || d === undefined) return '';
  const n = Number(d);
  if (!n) return '';
  return `${Number.isInteger(n) ? n : n.toFixed(1)}d`;
};

export default function ImportPlanPanel({ sowId, onClose, onImported }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [xml, setXml] = useState('');
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('append');
  const [collapsed, setCollapsed] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(''); setPreview(null); setBusy(true);
    try {
      const text = await file.text();
      const p = await api.post(`sows/${sowId}/project-tasks/import-preview`, { xml: text });
      setXml(text);
      setFileName(file.name);
      setPreview(p);
      setMode(p.existing_task_count > 0 ? 'append' : 'replace');
      // Start with the top two levels open: enough to recognise the plan,
      // not so much that a 140-row file is a wall.
      const c = {};
      for (const t of p.tasks) if (t.is_summary && t.level >= 2) c[t.uid] = true;
      setCollapsed(c);
    } catch (err) {
      setError(err.message || 'That file could not be read.');
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview || !xml) return;
    setBusy(true); setError('');
    try {
      const r = await api.post(`sows/${sowId}/project-tasks/import`, { xml, mode: mode });
      onImported(r);
    } catch (err) {
      setError(err.message || 'The import did not complete.');
      setBusy(false);
    }
  };

  const visible = useMemo(() => {
    if (!preview) return [];
    const hidden = new Set();
    const out = [];
    for (const t of preview.tasks) {
      if (t.parent_uid != null && (hidden.has(t.parent_uid))) { hidden.add(t.uid); continue; }
      if (collapsed[t.uid]) hidden.add(t.uid);
      out.push(t);
    }
    return out;
  }, [preview, collapsed]);

  const childCount = useMemo(() => {
    const m = {};
    if (!preview) return m;
    for (const t of preview.tasks) {
      let p = t.parent_uid;
      const seen = new Set();
      while (p != null && !seen.has(p)) { seen.add(p); m[p] = (m[p] || 0) + 1; p = preview.tasks.find(x => x.uid === p)?.parent_uid ?? null; }
    }
    return m;
  }, [preview]);

  const replacing = mode === 'replace' && preview?.existing_task_count > 0;
  const c = preview?.counts;

  return (
    <Modal title="Import plan from Microsoft Project" onClose={onClose} wide footer={
      <>
        {preview && (
          <button onClick={pick} disabled={busy}
                  className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white text-gray-600 mr-auto">
            Choose a different file
          </button>
        )}
        <button onClick={onClose} disabled={busy} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        {preview && (
          <button onClick={commit} disabled={busy}
                  className={`px-3 py-1 text-[10px] font-semibold rounded text-white disabled:opacity-50 ${replacing ? 'bg-red-600 hover:bg-red-700' : 'bg-navy hover:bg-navy/90'}`}>
            {busy ? 'Importing…' : replacing
              ? `Replace plan with ${c.tasks} task${c.tasks === 1 ? '' : 's'}`
              : `Import ${c.tasks} task${c.tasks === 1 ? '' : 's'}`}
          </button>
        )}
      </>
    }>
      <input ref={inputRef} type="file" accept=".xml" className="hidden" onChange={onFile} />

      {!preview && (
        <div className="py-6 text-center">
          <button onClick={pick} disabled={busy}
                  className="px-4 py-2 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/90 disabled:opacity-50">
            {busy ? 'Reading…' : 'Choose an XML file'}
          </button>
          <div className="mt-3 text-[9px] text-gray-500 max-w-md mx-auto leading-relaxed">
            In Project, use File → Save As and pick <span className="font-semibold">XML Format</span>.
            The outline, milestones, links with their lag, constraints and notes all come across.
            Resources do not: assign people in the plan afterwards. Dates are recalculated by the
            plan from the pinned start and the links, so they may differ slightly where Project
            used a different calendar.
          </div>
          {error && <div className="mt-3 text-[9px] text-red-600">{error}</div>}
        </div>
      )}

      {preview && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[11px] font-semibold text-navy">{preview.title || fileName}</div>
              <div className="text-[9px] text-gray-400">
                {fileName}
                {preview.start_date && <> · {fmtDY(preview.start_date)} → {fmtDY(preview.finish_date)}</>}
              </div>
            </div>
            <div className="text-[9px] text-gray-500">
              <span className="font-semibold text-navy">{c.tasks}</span> tasks
              {c.summaries > 0 && <> · {c.summaries} summar{c.summaries === 1 ? 'y' : 'ies'}</>}
              {c.milestones > 0 && <> · {c.milestones} milestone{c.milestones === 1 ? '' : 's'}</>}
              {c.links > 0 && <> · {c.links} link{c.links === 1 ? '' : 's'}</>}
              {c.skipped > 0 && <span className="text-amber-700"> · {c.skipped} skipped</span>}
            </div>
          </div>

          {preview.warnings?.length > 0 && (
            <ul className="text-[9px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2 space-y-0.5">
              {preview.warnings.map((w, i) => <li key={i}>⚠ {w.message}</li>)}
            </ul>
          )}

          <div className="border border-gray-200 rounded max-h-[40vh] overflow-y-auto">
            {visible.map(t => {
              const kids = childCount[t.uid] || 0;
              return (
                <div key={t.uid}
                     className={`flex items-center gap-1.5 px-2 py-0.5 border-b border-gray-100 last:border-b-0 text-[9px] ${t.is_summary ? 'bg-gray-50' : 'bg-white'}`}
                     style={{ paddingLeft: `${8 + (t.level - 1) * 14}px` }}>
                  {t.is_summary ? (
                    <button onClick={() => setCollapsed(s => ({ ...s, [t.uid]: !s[t.uid] }))}
                            className="w-3 text-[8px] text-gray-400 hover:text-navy flex-shrink-0">
                      {collapsed[t.uid] ? '▸' : '▾'}
                    </button>
                  ) : <span className="w-3 flex-shrink-0 text-center text-gold">{t.is_milestone ? '◆' : ''}</span>}
                  <span className={`flex-1 min-w-0 truncate ${t.is_summary ? 'font-semibold text-navy' : 'text-gray-700'}`} title={t.name}>
                    {t.name}
                    {!!t.is_summary && !!collapsed[t.uid] && <span className="text-gray-400 font-normal"> · {kids}</span>}
                  </span>
                  {t.has_notes && <span className="text-gray-300" title="Has notes">≡</span>}
                  {t.constraint_type !== 'asap' && (
                    <span className="px-1 rounded bg-blue-50 text-blue-700 text-[8px]" title="Kept from the file">
                      {t.constraint_type === 'mso' ? 'must start' : t.constraint_type === 'snet' ? 'not before' : 'finish by'} {fmtDY(t.constraint_date)}
                    </span>
                  )}
                  {t.predecessors > 0 && <span className="text-gray-400 w-8 text-right" title="Predecessors">{t.predecessors}→</span>}
                  <span className="text-gray-400 w-8 text-right">{fmtDays(t.duration_days)}</span>
                  <span className="text-gray-400 w-20 text-right">{t.start ? fmtDY(t.start) : ''}</span>
                </div>
              );
            })}
          </div>

          {preview.existing_task_count > 0 && (
            <div className="border border-gray-200 rounded px-3 py-2 space-y-1.5">
              <div className="text-[9px] text-gray-500">
                This plan already has <span className="font-semibold text-navy">{preview.existing_task_count}</span> task{preview.existing_task_count === 1 ? '' : 's'}.
              </div>
              <label className="flex items-start gap-2 text-[9px] cursor-pointer">
                <input type="radio" name="import-mode" className="mt-0.5 accent-navy" checked={mode === 'append'} onChange={() => setMode('append')} />
                <span><span className="font-semibold text-navy">Add below the current plan.</span> <span className="text-gray-500">Nothing here changes; the imported tasks go underneath.</span></span>
              </label>
              <label className="flex items-start gap-2 text-[9px] cursor-pointer">
                <input type="radio" name="import-mode" className="mt-0.5 accent-red-600" checked={mode === 'replace'} onChange={() => setMode('replace')} />
                <span><span className="font-semibold text-red-700">Replace the current plan.</span> <span className="text-gray-500">Deletes the {preview.existing_task_count} existing task{preview.existing_task_count === 1 ? '' : 's'} and their links. Booked hours and payment milestones are kept, but no longer point at a task. Baselines are unaffected.</span></span>
              </label>
            </div>
          )}

          {error && <div className="text-[9px] text-red-600">{error}</div>}
        </div>
      )}
    </Modal>
  );
}
